import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button, Input, Chip, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { validateWorkflow } from "@/utils/playground/playgroundValidation";
import { logger } from "@/telemetry";

interface PlaygroundToolbarProps {
  onAddAgent: () => void;
  onAddTeam: () => void;
  onOpenAgentCatalogue: () => void;
  onAutoLayout: () => void;
  onSave: () => void;
  onRun: () => void;
  onStop: () => void;
  onOpenWorkflowSelector: () => void;
  isSaving: boolean;
}

const PlaygroundToolbar: React.FC<PlaygroundToolbarProps> = ({
  onAddAgent,
  onAddTeam,
  onOpenAgentCatalogue,
  onAutoLayout,
  onSave,
  onRun,
  onStop,
  onOpenWorkflowSelector,
  isSaving,
}) => {
  const { t } = useTranslation();
  const { workflow, setWorkflowName } = useWorkflowStore();
  const {
    hasUnsavedChanges,
    validationErrors,
    isValidating,
    isRunning,
    isMessagesPanelOpen,
    toggleMessagesPanel,
    setValidationErrors,
    setNodeValidationErrors,
    setIsValidating,
  } = usePlaygroundStore();
  const { platformClient } = useWebuildAPIClient();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editableName, setEditableName] = useState("");
  const validateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasValidatedRef = useRef(false);

  // Auto-validate on workflow changes (debounced, immediate on first load)
  useEffect(() => {
    if (validateTimerRef.current) {
      clearTimeout(validateTimerRef.current);
    }

    // Run immediately on first load, debounce subsequent changes
    const delay = hasValidatedRef.current ? 800 : 0;

    validateTimerRef.current = setTimeout(async () => {
      if (!workflow || !platformClient) return;

      hasValidatedRef.current = true;
      setIsValidating(true);
      try {
        const result = await validateWorkflow(workflow, platformClient);

        setValidationErrors(result.errors);
        setNodeValidationErrors(result.nodeErrors);
      } catch (error) {
        logger.error("Validation error", error);
        // Clear errors on validation failure so the UI does not stay stuck
        setValidationErrors([]);
        setNodeValidationErrors({});
      } finally {
        setIsValidating(false);
      }
    }, delay);

    return () => {
      if (validateTimerRef.current) {
        clearTimeout(validateTimerRef.current);
      }
    };
  }, [workflow, platformClient, setValidationErrors, setNodeValidationErrors, setIsValidating]);

  const handleNameSubmit = useCallback(() => {
    if (editableName.trim()) {
      setWorkflowName(editableName.trim());
    }
    setIsEditingName(false);
  }, [editableName, setWorkflowName]);

  const startEditingName = useCallback(() => {
    setEditableName(workflow?.name || "");
    setIsEditingName(true);
  }, [workflow?.name]);

  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-default-200 bg-default-50/50 backdrop-blur-sm">
      {/* Left: Workflow name + selector */}
      <div className="flex items-center gap-3">
        <Button
          isIconOnly
          aria-label={t("Select workflow")}
          size="sm"
          variant="flat"
          onPress={onOpenWorkflowSelector}
        >
          <Icon className="w-4 h-4" icon="solar:folder-open-linear" />
        </Button>

        {isEditingName ? (
          <Input
            autoFocus
            className="w-60"
            size="sm"
            value={editableName}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleNameSubmit();
              if (e.key === "Escape") setIsEditingName(false);
            }}
            onValueChange={setEditableName}
          />
        ) : (
          <button
            className="text-sm font-semibold text-default-800 hover:text-primary cursor-pointer px-2 py-1 rounded hover:bg-default-100 transition-colors"
            title={t("Click to rename")}
            onClick={startEditingName}
          >
            {workflow?.name || t("New Workflow")}
          </button>
        )}

        {hasUnsavedChanges && (
          <Chip color="warning" size="sm" variant="flat">
            {t("Unsaved")}
          </Chip>
        )}

        {/* Validation indicator */}
        {isValidating ? (
          <Chip
            size="sm"
            startContent={
              <Icon className="w-3 h-3 animate-spin" icon="solar:refresh-circle-linear" />
            }
            variant="flat"
          >
            {t("Validating...")}
          </Chip>
        ) : validationErrors.length === 0 ? (
          <Tooltip content={t("Workflow is valid")}>
            <Chip
              color="success"
              size="sm"
              startContent={<Icon className="w-3 h-3" icon="solar:check-circle-linear" />}
              variant="flat"
            >
              {t("Valid")}
            </Chip>
          </Tooltip>
        ) : (
          <Tooltip
            content={
              <div className="max-w-xs">
                {validationErrors.map((err, i) => (
                  <p key={i} className="text-xs">
                    {err}
                  </p>
                ))}
              </div>
            }
          >
            <Chip
              color="danger"
              size="sm"
              startContent={<Icon className="w-3 h-3" icon="solar:close-circle-linear" />}
              variant="flat"
            >
              {validationErrors.length} {validationErrors.length === 1 ? t("error") : t("errors")}
            </Chip>
          </Tooltip>
        )}
      </div>

      {/* Centre: Add node buttons */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          startContent={<Icon className="w-4 h-4" icon="solar:user-hand-up-outline" />}
          variant="flat"
          onPress={onAddAgent}
        >
          {t("Agent")}
        </Button>
        <Button
          size="sm"
          startContent={<Icon className="w-4 h-4" icon="solar:users-group-rounded-linear" />}
          variant="flat"
          onPress={onAddTeam}
        >
          {t("Team")}
        </Button>
        <div className="w-px h-6 bg-default-200 mx-1" />
        <Button
          size="sm"
          startContent={<Icon className="w-4 h-4" icon="solar:download-minimalistic-linear" />}
          variant="flat"
          onPress={onOpenAgentCatalogue}
        >
          {t("Import Agent")}
        </Button>
        <div className="w-px h-6 bg-default-200 mx-1" />

        <Tooltip content={t("Auto-arrange nodes")}>
          <Button
            isIconOnly
            aria-label={t("Auto-arrange")}
            size="sm"
            variant="flat"
            onPress={onAutoLayout}
          >
            <Icon className="w-4 h-4" icon="solar:sort-from-top-to-bottom-linear" />
          </Button>
        </Tooltip>
      </div>

      {/* Right: Run/Stop + Messages + Save */}
      <div className="flex items-center gap-2">
        <Tooltip content={isMessagesPanelOpen ? t("Hide output") : t("Show output")}>
          <Button
            isIconOnly
            aria-label={t("Toggle output panel")}
            color={isMessagesPanelOpen ? "primary" : "default"}
            size="sm"
            variant={isMessagesPanelOpen ? "flat" : "light"}
            onPress={toggleMessagesPanel}
          >
            <Icon className="w-4 h-4" icon="solar:chat-square-linear" />
          </Button>
        </Tooltip>

        <div className="w-px h-6 bg-default-200 mx-1" />

        {isRunning ? (
          <Button
            color="danger"
            size="sm"
            startContent={<Icon className="w-4 h-4" icon="solar:stop-circle-linear" />}
            onPress={onStop}
          >
            {t("Stop")}
          </Button>
        ) : (
          <Tooltip
            content={
              !workflow || !Object.keys(workflow.agents || {}).length
                ? t("Add at least one agent to run")
                : validationErrors.length > 0
                  ? t("Fix validation errors before running")
                  : t("Run workflow")
            }
          >
            <div>
              <Button
                color="success"
                isDisabled={
                  !workflow ||
                  !Object.keys(workflow.agents || {}).length ||
                  validationErrors.length > 0 ||
                  isValidating
                }
                size="sm"
                startContent={<Icon className="w-4 h-4" icon="solar:play-circle-linear" />}
                variant="flat"
                onPress={onRun}
              >
                {t("Run")}
              </Button>
            </div>
          </Tooltip>
        )}

        <Button
          color="primary"
          isDisabled={!workflow || isSaving}
          isLoading={isSaving}
          size="sm"
          startContent={!isSaving && <Icon className="w-4 h-4" icon="solar:diskette-linear" />}
          onPress={onSave}
        >
          {isSaving ? t("Saving...") : t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default PlaygroundToolbar;
